import { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { MessageCircle } from 'lucide-react';

const WHATSAPP_NUMBER = "919535834444";
const WHATSAPP_MESSAGE = "Hi! I'm interested in your products. Can you please share more details?";

const collections = [
  {
    id: 1,
    name: 'Langa',
    image: 'https://images.unsplash.com/photo-1767955694884-d4bf352c23c2?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NjV8MHwxfHNlYXJjaHwxfHxsdXh1cnklMjBJbmRpYW4lMjBsZWhlbmdhJTIwYnJpZGFsJTIwZmFzaGlvbnxlbnwwfHx8fDE3NzI5ODYwOTB8MA&ixlib=rb-4.1.0&q=85',
    size: 'large',
  },
  {
    id: 2,
    name: 'Crop Top',
    image: 'https://images.unsplash.com/photo-1610047614301-13c63f00c032?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NjV8MHwxfHNlYXJjaHwyfHxJbmRpYW4lMjBjcm9wJTIwdG9wJTIwZXRobmljJTIwZmFzaGlvbnxlbnwwfHx8fDE3NzI5ODYwOTB8MA&ixlib=rb-4.1.0&q=85',
    size: 'small',
  },
  {
    id: 3,
    name: 'Gown',
    image: 'https://images.unsplash.com/photo-1767790693645-2373e54d4f02?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NjV8MHwxfHNlYXJjaHwyfHxsdXh1cnklMjBJbmRpYW4lMjBsZWhlbmdhJTIwYnJpZGFsJTIwZmFzaGlvbnxlbnwwfHx8fDE3NzI5ODYwOTB8MA&ixlib=rb-4.1.0&q=85',
    size: 'small',
  },
  {
    id: 4,
    name: 'Plazo Jacket',
    image: 'https://images.pexels.com/photos/28943676/pexels-photo-28943676.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940',
    size: 'medium',
  },
  {
    id: 5,
    name: 'Dress Material',
    image: 'https://images.unsplash.com/photo-1760613129745-418b15f91d56?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NjV8MHwxfHNlYXJjaHw0fHxsdXh1cnklMjBJbmRpYW4lMjBsZWhlbmdhJTIwYnJpZGFsJTIwZmFzaGlvbnxlbnwwfHx8fDE3NzI5ODYwOTB8MA&ixlib=rb-4.1.0&q=85',
    size: 'medium',
  },
  {
    id: 6,
    name: 'Ready-Made Dress',
    image: 'https://images.pexels.com/photos/5814563/pexels-photo-5814563.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940',
    size: 'large',
  },
  {
    id: 7,
    name: 'Tops',
    image: 'https://images.unsplash.com/photo-1750481890787-964db32a0d74?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NjV8MHwxfHNlYXJjaHwxfHxJbmRpYW4lMjBmYXNoaW9uJTIwdG9wc3xlbnwwfHx8fDE3NzI5ODYwOTB8MA&ixlib=rb-4.1.0&q=85',
    size: 'small',
  },
  {
    id: 8,
    name: 'Cordset',
    image: 'https://images.unsplash.com/photo-1717835429626-d6dd3f2c9afb?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNzl8MHwxfHNlYXJjaHwzfHxJbmRpYW4lMjBtb2RlbCUyMHNhcmVlJTIwbHV4dXJ5JTIwZmFzaGlvbiUyMHBvcnRyYWl0JTIwcmVkJTIwZ29sZHxlbnwwfHx8fDE3NzI5ODYwODZ8MA&ixlib=rb-4.1.0&q=85',
    size: 'small',
  },
  {
    id: 9,
    name: 'Ready-Made Sarees',
    image: 'https://images.pexels.com/photos/5814563/pexels-photo-5814563.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940',
    size: 'medium',
  },
];

const CollectionCard = ({ collection, index }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });

  const getWhatsAppLink = (productName) => {
    const message = encodeURIComponent(`Hi! I'm interested in ${productName} from Devanand Vestment. Can you please share more details?`);
    return `https://wa.me/${WHATSAPP_NUMBER}?text=${message}`;
  };

  return (
    <motion.div
      ref={ref}
      className={`relative overflow-hidden group cursor-pointer ${
        collection.size === 'large' ? 'md:col-span-2 md:row-span-2' : 
        collection.size === 'medium' ? 'md:col-span-2' : ''
      }`}
      initial={{ opacity: 0, y: 50 }}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.6, delay: index * 0.1 }}
      whileHover={{ scale: 1.02 }}
      style={{ transformStyle: 'preserve-3d' }}
      data-testid={`collection-card-${collection.name.toLowerCase().replace(/\s+/g, '-')}`}
    >
      {/* 3D Tilt Effect Container */}
      <motion.div
        className="relative h-64 md:h-80 lg:h-96 overflow-hidden rounded-2xl"
        style={{ 
          transformStyle: 'preserve-3d', 
          perspective: 1000,
          boxShadow: '0 10px 40px -10px rgba(128, 0, 32, 0.3), 0 0 25px rgba(212, 175, 55, 0.2)'
        }}
        whileHover={{ 
          rotateX: 5, 
          rotateY: 5,
          transition: { duration: 0.3 }
        }}
      >
        {/* Image */}
        <motion.img
          src={collection.image}
          alt={collection.name}
          className="w-full h-full object-cover"
          whileHover={{ scale: 1.1 }}
          transition={{ duration: 0.6 }}
        />

        {/* Red Overlay */}
        <motion.div
          className="absolute inset-0 bg-gradient-to-t from-[#800020]/90 via-[#800020]/40 to-transparent"
          initial={{ opacity: 0 }}
          whileHover={{ opacity: 1 }}
          transition={{ duration: 0.4 }}
        />

        {/* Gold Border Glow */}
        <motion.div
          className="absolute inset-0 pointer-events-none rounded-2xl"
          initial={{ opacity: 0 }}
          whileHover={{ opacity: 1 }}
          transition={{ duration: 0.3 }}
          style={{
            boxShadow: 'inset 0 0 30px rgba(212, 175, 55, 0.5), 0 0 40px rgba(212, 175, 55, 0.4)',
            border: '3px solid rgba(212, 175, 55, 0.7)',
            borderRadius: '1rem',
          }}
        />

        {/* Content */}
        <div className="absolute inset-0 flex flex-col justify-end p-6">
          <motion.h3
            className="font-heading text-xl md:text-2xl lg:text-3xl text-white mb-2"
            initial={{ y: 20, opacity: 0 }}
            whileHover={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.3 }}
          >
            {collection.name}
          </motion.h3>

          {/* WhatsApp Button */}
          <motion.a
            href={getWhatsAppLink(collection.name)}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-4 py-2 bg-[#25D366] text-white font-body text-sm rounded-full w-fit opacity-0 group-hover:opacity-100 transition-opacity duration-300"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={(e) => e.stopPropagation()}
            data-testid={`whatsapp-btn-${collection.name.toLowerCase().replace(/\s+/g, '-')}`}
          >
            <MessageCircle size={18} />
            <span>Enquire Now</span>
          </motion.a>
        </div>
      </motion.div>
    </motion.div>
  );
};

export const FeaturedCollections = () => {
  const sectionRef = useRef(null);
  const isInView = useInView(sectionRef, { once: true, margin: '-100px' });

  return (
    <section
      id="collections"
      ref={sectionRef}
      className="py-24 md:py-32 bg-[#FDFBF7]"
      data-testid="featured-collections-section"
    >
      <div className="max-w-[1920px] mx-auto px-4 md:px-8 lg:px-12">
        {/* Section Header */}
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
        >
          <motion.span
            className="font-accent text-sm tracking-[0.3em] text-[#D4AF37] uppercase block mb-4"
            initial={{ opacity: 0 }}
            animate={isInView ? { opacity: 1 } : {}}
            transition={{ delay: 0.2 }}
          >
            Explore Our
          </motion.span>
          <h2 className="font-heading text-4xl md:text-5xl lg:text-6xl text-[#1A1A1A]">
            Featured Collections
          </h2>
          <motion.div
            className="w-24 h-0.5 bg-[#D4AF37] mx-auto mt-6"
            initial={{ scaleX: 0 }}
            animate={isInView ? { scaleX: 1 } : {}}
            transition={{ duration: 0.8, delay: 0.4 }}
          />
        </motion.div>

        {/* Bento Grid */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 md:gap-6">
          {collections.map((collection, index) => (
            <CollectionCard
              key={collection.id}
              collection={collection}
              index={index}
            />
          ))}
        </div>
      </div>
    </section>
  );
};
